import { UnhandledHttpResponseError } from '@garner-health/api-client';
import { GarnerCareGoalsClientV2 } from '@garner-health/api-client-svc-clinical-attributes/care-goals-api-v2';
import { objectToCamel } from '@garner-health/lib-ts-common';

import createLogger from '~/logging';

import { careGoals503Counter } from './care-goals-metrics';
import { baseClientOptions, getDefaultHeaders } from './common';

const log = createLogger(__filename);

export type CareGoalType = 'specialty' | 'procedure' | 'symptom' | 'condition';

export type CareGoal = {
  id: string;
  type: CareGoalType;
  term: string;
  metricIds: (string | null)[];
  supportsProviderSearch: boolean;
  specialty: string | null;
  includeApps: boolean;
};

class CareGoalsClient {
  private readonly client: GarnerCareGoalsClientV2;

  constructor() {
    this.client = new GarnerCareGoalsClientV2(baseClientOptions);
  }

  async getCareGoals(params: { query: string } | { id: string }): Promise<CareGoal[]> {
    const baseQuery = 'query' in params ? params : { id: [params.id] };
    const headers = await getDefaultHeaders();

    return this.client
      .get('/care-goals', { headers, query: { ...baseQuery, is_recommendable: true, language: 'en-US' } })
      .onStatus(
        200,
        r =>
          objectToCamel(r.json.items)
            .map(careGoal => ({
              ...careGoal,
              type: careGoal.type.toLowerCase(),
            }))
            .filter(cg => cg.supportsProviderSearch), // Filter out unsupported care goals
      )
      .onStatus(422, err => {
        log.error({ err, context: { params } }, 'Unable to get care goals with provided params');
        return [];
      })
      .catch((err: unknown) => {
        if (err instanceof UnhandledHttpResponseError && err.response.status === 503) {
          careGoals503Counter.add(1, { calling_service: 'ui-datapro', endpoint: 'get-care-goals' });
          log.error(
            { context: { params, status: 503 } },
            'Clinical attributes service unavailable (503) when fetching care goals for datapro search',
          );
          throw Error('Clinical attributes service unavailable');
        }
        log.error({ err, context: params }, 'Error getting care goals');
        throw Error('Error getting care goals');
      });
  }
}

export const careGoalsClient = new CareGoalsClient();
